import { ThunkAction } from "redux-thunk";
import { AxiosResponse } from "axios";
import ApiClient, { createTokenConfig } from "api";
import { alertErrorHandler } from "actions/ActionUtils";
import { AlertAction } from "actions/alerts/AlertsActionsTypes";
import { showSuccessAlert } from "actions/alerts/AlertsActions";
import { SystemEmailTemplatesState } from "reducers/systemEmailTemplates/SystemEmailTemplatesState";
import { EmailTemplateSavePayload, SystemEmailTemplate } from "model/SystemEmailTemplate";
import {
  EmailEditorConfigDto,
  SystemEmailTemplatesIndexResponseDto,
  SystemEmailTemplateShowResponseDto
} from "./SystemEmailTemplatesDtos";
import {
  convertEmailEditorConfig,
  convertSavePayloadToDto,
  convertSystemEmailTemplate
} from "./SystemEmailTemplatesConverter";
import {
  SystemEmailTemplatesAction,
  SYSTEM_EMAIL_TEMPLATES_ERROR_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_CONFIG_START_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_CONFIG_SUCCESS_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_LIST_START_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_LIST_SUCCESS_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_TEMPLATE_START_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_TEMPLATE_SUCCESS_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_RESET_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_SAVE_START_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_SAVE_SUCCESS_ACTION_TYPE
} from "./SystemEmailTemplatesActionsTypes";

const listUrl   = "admin/system_email_templates";
const configUrl = "admin/email_editor/config";

type SystemEmailTemplatesThunk<R = void> = ThunkAction<
  R,
  SystemEmailTemplatesState,
  null,
  SystemEmailTemplatesAction | AlertAction
>;

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Fetch the full list of system templates (lightweight shape — no html/design_json). */
export const fetchListThunk = (): SystemEmailTemplatesThunk => {
  return async dispatch => {
    dispatch(fetchListStart());
    try {
      const response: AxiosResponse<SystemEmailTemplatesIndexResponseDto> =
        await ApiClient.get(listUrl, createTokenConfig());
      dispatch(fetchListSuccess(response.data.email_templates.map(convertSystemEmailTemplate)));
    } catch (apiError) {
      alertErrorHandler(apiError, dispatch, "Unable to load email templates.");
      dispatch(error());
    }
  };
};

/** Fetch a single template with html + design_json (used by preview + editor). */
export const fetchTemplateThunk = (id: number): SystemEmailTemplatesThunk => {
  return async dispatch => {
    dispatch(fetchTemplateStart());
    try {
      const response: AxiosResponse<SystemEmailTemplateShowResponseDto> =
        await ApiClient.get(`${listUrl}/${id}`, createTokenConfig());
      dispatch(fetchTemplateSuccess(convertSystemEmailTemplate(response.data.email_template)));
    } catch (apiError) {
      alertErrorHandler(apiError, dispatch, "Unable to load email template.");
      dispatch(error());
    }
  };
};

/** Fetch the Unlayer bootstrap config (project id + signed user identity + merge tags). */
export const fetchEditorConfigThunk = (): SystemEmailTemplatesThunk => {
  return async dispatch => {
    dispatch(fetchConfigStart());
    try {
      const response: AxiosResponse<EmailEditorConfigDto> = await ApiClient.get(
        configUrl,
        createTokenConfig()
      );
      dispatch(fetchConfigSuccess(convertEmailEditorConfig(response.data)));
    } catch (apiError) {
      alertErrorHandler(apiError, dispatch, "Unable to load email editor configuration.");
      dispatch(error());
    }
  };
};

/**
 * Create a new system template. Resolves to the saved template on success
 * (or `null` on failure) so the caller can navigate / close the editor.
 */
export const saveTemplateThunk = (
  payload: EmailTemplateSavePayload
): SystemEmailTemplatesThunk<Promise<SystemEmailTemplate | null>> => {
  return async dispatch => {
    dispatch(saveStart());
    try {
      const response: AxiosResponse<SystemEmailTemplateShowResponseDto> = await ApiClient.post(
        listUrl,
        { email_template: convertSavePayloadToDto(payload) },
        createTokenConfig()
      );
      const saved = convertSystemEmailTemplate(response.data.email_template);
      dispatch(saveSuccess(saved));
      dispatch(showSuccessAlert("Template created."));
      return saved;
    } catch (apiError) {
      alertErrorHandler(apiError, dispatch, "Unable to save template.");
      dispatch(error());
      return null;
    }
  };
};

/** Update an existing system template. Same return contract as `saveTemplateThunk`. */
export const updateTemplateThunk = (
  id: number,
  payload: EmailTemplateSavePayload
): SystemEmailTemplatesThunk<Promise<SystemEmailTemplate | null>> => {
  return async dispatch => {
    dispatch(saveStart());
    try {
      const response: AxiosResponse<SystemEmailTemplateShowResponseDto> = await ApiClient.patch(
        `${listUrl}/${id}`,
        { email_template: convertSavePayloadToDto(payload) },
        createTokenConfig()
      );
      const saved = convertSystemEmailTemplate(response.data.email_template);
      dispatch(saveSuccess(saved));
      dispatch(showSuccessAlert("Template updated."));
      return saved;
    } catch (apiError) {
      alertErrorHandler(apiError, dispatch, "Unable to update template.");
      dispatch(error());
      return null;
    }
  };
};

/**
 * Toggle the published flag from the row action. Sends a minimal payload —
 * html/design_json are NOT resent, so a stale in-memory row can't clobber
 * template content by accident. Skips the global `saving` flag; callers show
 * per-row loading locally.
 */
export const togglePublishThunk = (
  id: number,
  published: boolean
): SystemEmailTemplatesThunk<Promise<SystemEmailTemplate | null>> => {
  return async dispatch => {
    try {
      const response: AxiosResponse<SystemEmailTemplateShowResponseDto> = await ApiClient.patch(
        `${listUrl}/${id}`,
        { email_template: { published } },
        createTokenConfig()
      );
      const saved = convertSystemEmailTemplate(response.data.email_template);
      dispatch(saveSuccess(saved));
      dispatch(showSuccessAlert(published ? "Template published." : "Template unpublished."));
      return saved;
    } catch (apiError) {
      alertErrorHandler(apiError, dispatch, "Unable to update template status.");
      return null;
    }
  };
};

/** Resets `currentTemplate` so reopening the editor for "Create" starts blank. */
export const reset = (): SystemEmailTemplatesAction => ({
  type: SYSTEM_EMAIL_TEMPLATES_RESET_ACTION_TYPE
});

// ---------------------------------------------------------------------------
// Action creators (sync, internal)
// ---------------------------------------------------------------------------

const fetchListStart = (): SystemEmailTemplatesAction => ({
  type: SYSTEM_EMAIL_TEMPLATES_FETCH_LIST_START_ACTION_TYPE
});

const fetchListSuccess = (list: Array<SystemEmailTemplate>): SystemEmailTemplatesAction => ({
  type: SYSTEM_EMAIL_TEMPLATES_FETCH_LIST_SUCCESS_ACTION_TYPE,
  payload: { list }
});

const fetchTemplateStart = (): SystemEmailTemplatesAction => ({
  type: SYSTEM_EMAIL_TEMPLATES_FETCH_TEMPLATE_START_ACTION_TYPE
});

const fetchTemplateSuccess = (template: SystemEmailTemplate): SystemEmailTemplatesAction => ({
  type: SYSTEM_EMAIL_TEMPLATES_FETCH_TEMPLATE_SUCCESS_ACTION_TYPE,
  payload: { template }
});

const fetchConfigStart = (): SystemEmailTemplatesAction => ({
  type: SYSTEM_EMAIL_TEMPLATES_FETCH_CONFIG_START_ACTION_TYPE
});

const fetchConfigSuccess = (config: import("model/SystemEmailTemplate").EmailEditorConfig): SystemEmailTemplatesAction => ({
  type: SYSTEM_EMAIL_TEMPLATES_FETCH_CONFIG_SUCCESS_ACTION_TYPE,
  payload: { config }
});

const saveStart = (): SystemEmailTemplatesAction => ({
  type: SYSTEM_EMAIL_TEMPLATES_SAVE_START_ACTION_TYPE
});

const saveSuccess = (template: SystemEmailTemplate): SystemEmailTemplatesAction => ({
  type: SYSTEM_EMAIL_TEMPLATES_SAVE_SUCCESS_ACTION_TYPE,
  payload: { template }
});

const error = (): SystemEmailTemplatesAction => ({
  type: SYSTEM_EMAIL_TEMPLATES_ERROR_ACTION_TYPE
});
